/* Product-specific size help. No cart writes, network requests, or generic size ladders. */
(() => {
  'use strict';

  /** @param {string} label @returns {number|null} Inch-based numeric option, or null. */
  const parseLength = (label) => {
    const match = String(label).trim().match(/^(\d+(?:\.\d+)?)\s*(?:"|″|in|inch|inches)?$/i);
    return match && Number(match[1]) > 0 ? Number(match[1]) : null;
  };

  /** @param {string} raw @returns {number|null} Strict positive decimal input. */
  const parseMeasurement = (raw) => {
    const text = String(raw).trim().replace(',', '.');
    return /^\d+(?:\.\d+)?$/.test(text) && Number(text) > 0 && Number.isFinite(Number(text)) ? Number(text) : null;
  };

  /** @param {{values:string[],raw:string,unit:string,ease:number}} args */
  const calculate = ({ values, raw, unit = 'in', ease = 0 }) => {
    const measurement = parseMeasurement(raw);
    if (measurement === null) return { state: 'invalid' };
    const entries = values.map((value) => ({ value, inches: parseLength(value) })).filter((entry) => entry.inches !== null).sort((a, b) => a.inches - b.inches);
    if (!entries.length) return { state: 'no_numeric' };
    const target = (unit === 'cm' ? measurement / 2.54 : measurement) + ease;
    const minimum = entries[0].inches;
    const maximum = entries[entries.length - 1].inches;
    if (target < minimum - 0.001 || target > maximum + 0.001) return { state: 'out_of_range', target, minimum, maximum };
    const entry = entries.find((candidate) => candidate.inches >= target - 0.001);
    return { state: 'size', ...entry, target, minimum, maximum };
  };

  /** @param {object[]} variants @param {string[]} selected @param {number} position @param {string} value */
  const exactVariant = (variants, selected, position, value) => {
    const desired = selected.slice();
    desired[position - 1] = value;
    return variants.find((variant) => desired.length > 0 && desired.every((item, index) => item != null && (variant.options?.[index] ?? variant[`option${index + 1}`]) === item)) || null;
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = { parseLength, parseMeasurement, calculate, exactVariant };
  if (typeof window === 'undefined' || !window.customElements || window.customElements.get('skrt-product-size-guide')) return;

  class SkrtProductSizeGuide extends HTMLElement {
    #abort;
    #data;
    #unit = 'in';
    #ease = 0;
    #choice = null;
    #source = 'manual';
    #calculation = null;
    #initialized = false;

    connectedCallback() {
      this.#abort?.abort();
      this.#abort = new AbortController();
      try {
        this.#data = JSON.parse(this.querySelector('[data-guide-data]')?.textContent || '{}');
      } catch (_) {
        return;
      }
      if (!Array.isArray(this.#data.values) || !Array.isArray(this.#data.variants)) return;
      if (!this.#initialized) this.#ease = ['bracelet', 'anklet'].includes(this.dataset.kind) ? 0.5 : 0;
      this.#initialized = true;
      const signal = this.#abort.signal;
      this.addEventListener('click', this.#onClick, { signal });
      this.addEventListener('input', this.#onInput, { signal });
      // The outer theme picker treats every bubbled change as a product option change.
      this.addEventListener('change', (event) => event.stopPropagation(), { signal });
      this.addEventListener('keydown', this.#onRadioKey, { signal });
      const modal = this.#modal;
      modal?.addEventListener('modal:afterShow', this.#onOpen, { signal });
      modal?.addEventListener('modal:afterHide', this.#restoreParentFocus, { signal });
      modal?.addEventListener('keydown', this.#onModalEscape, { signal });
      modal?.addEventListener('keyup', this.#onModalEscape, { signal });
      this.#refreshStock();
    }

    disconnectedCallback() {
      this.#abort?.abort();
    }

    get #modal() { return document.getElementById(this.dataset.modalId); }
    get #picker() { return document.getElementById(this.dataset.pickerId); }
    get #position() { return Number(this.dataset.optionPosition); }
    get #input() { return this.querySelector('[data-guide-input]'); }
    get #applyButton() { return this.querySelector('[data-guide-apply]'); }

    /** @returns {string[]} Current real picker values, never the guide's buttons. */
    #selectedOptions() {
      const selected = (this.#data.selectedOptions || []).slice();
      const picker = this.#picker;
      if (!picker) return selected;
      for (const control of picker.querySelectorAll('input[data-option-index]:checked, select[data-option-index]')) {
        if (control.closest('skrt-product-size-guide')) continue;
        const position = Number(String(control.dataset.optionIndex).replace('option', ''));
        if (position > 0) selected[position - 1] = control.value;
      }
      // Some theme dropdowns keep the position on option elements, not the select.
      for (const select of picker.querySelectorAll('select')) {
        if (select.closest('skrt-product-size-guide')) continue;
        const option = select.selectedOptions[0];
        const position = Number(option?.dataset.optionIndex);
        if (position > 0) selected[position - 1] = select.value;
      }
      return selected;
    }

    #variant(value) {
      return exactVariant(this.#data.variants, this.#selectedOptions(), this.#position, value);
    }

    #text(key) { return this.#data.strings[key] || ''; }
    #format(value) { return String(Math.round(value * 100) / 100); }
    #bothUnits(inches) { return `${this.#format(inches)}″ / ${this.#format(inches * 2.54)} cm`; }

    #onOpen = () => {
      this.#refreshStock();
      if (this.#choice) this.#renderResult();
    };

    #restoreParentFocus = () => {
      // Concept has one global trap. Restore the parent after the child's queued cleanup.
      setTimeout(() => {
        const parent = this.#picker?.closest('quick-view[open]');
        if (parent && typeof theme !== 'undefined' && theme.a11y?.trapFocus) {
          const trigger = parent.querySelector(`[aria-controls="${CSS.escape(this.dataset.modalId)}"]`);
          theme.a11y.trapFocus(parent, trigger || parent.focusElement || parent);
        }
      }, 0);
    };

    #onModalEscape = (event) => {
      if (event.key !== 'Escape' && event.code !== 'Escape') return;
      // Consume both events; the theme's document keyup otherwise also closes Quick Buy.
      event.stopPropagation();
      event.preventDefault();
      if (event.type === 'keyup' && this.#modal?.hasAttribute('open')) this.#modal.hide?.();
    };

    #onInput = (event) => {
      if (event.target !== this.#input) return;
      event.stopPropagation();
      this.#compute();
    };

    #onClick = (event) => {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-guide-unit')) {
        const nextUnit = button.dataset.guideUnit;
        const measurement = parseMeasurement(this.#input?.value || '');
        if (measurement !== null && nextUnit !== this.#unit) this.#input.value = this.#format(nextUnit === 'cm' ? measurement * 2.54 : measurement / 2.54);
        this.#unit = nextUnit;
        this.#setRadio(button);
        if (this.#input?.value) this.#compute();
      } else if (button.hasAttribute('data-guide-ease')) {
        this.#ease = Number(button.dataset.guideEase);
        this.#setRadio(button);
        if (this.#input?.value) this.#compute();
      } else if (button.hasAttribute('data-guide-value')) {
        this.#source = 'manual';
        this.#calculation = null;
        this.#choice = button.dataset.guideValue;
        this.#input?.removeAttribute('aria-invalid');
        this.#renderResult();
        this.#revealResult();
      } else if (button.hasAttribute('data-guide-apply')) {
        this.#apply();
      }
    };

    #revealResult() {
      requestAnimationFrame(() => {
        const result = this.querySelector('[data-guide-result]');
        const scroller = this.closest('.drawer__scrollable');
        if (!this.isConnected || !result || result.hidden || !scroller) return;
        const resultBounds = result.getBoundingClientRect();
        const scrollBounds = scroller.getBoundingClientRect();
        const inset = parseFloat(getComputedStyle(this).rowGap) || 0;
        let delta = 0;
        if (resultBounds.height > scrollBounds.height - inset * 2 || resultBounds.top < scrollBounds.top + inset) {
          delta = resultBounds.top - scrollBounds.top - inset;
        } else if (resultBounds.bottom > scrollBounds.bottom - inset) {
          delta = resultBounds.bottom - scrollBounds.bottom + inset;
        }
        if (!delta) return;
        scroller.scrollTo({
          top: scroller.scrollTop + delta,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      });
    }

    #onRadioKey = (event) => {
      if (event.target === this.#input && event.key === 'Enter') {
        // A bundle picker can be inside a form. Enter must calculate, never submit it.
        event.preventDefault();
        event.stopPropagation();
        this.#compute();
        return;
      }
      const radio = event.target.closest('[role="radio"]');
      const group = radio?.closest('[role="radiogroup"]');
      if (!radio || !group) return;
      const radios = Array.from(group.querySelectorAll('[role="radio"]'));
      const index = radios.indexOf(radio);
      let next = index;
      if (['ArrowRight', 'ArrowDown'].includes(event.key)) next = (index + 1) % radios.length;
      else if (['ArrowLeft', 'ArrowUp'].includes(event.key)) next = (index - 1 + radios.length) % radios.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = radios.length - 1;
      else return;
      event.preventDefault();
      event.stopPropagation();
      radios[next].focus();
      radios[next].click();
    };

    #setRadio(button) {
      const group = button.closest('[role="radiogroup"]');
      for (const sibling of group.querySelectorAll('[role="radio"]')) {
        const selected = sibling === button;
        sibling.setAttribute('aria-checked', String(selected));
        sibling.tabIndex = selected ? 0 : -1;
      }
    }

    #refreshStock() {
      for (const button of this.querySelectorAll('[data-guide-value]')) {
        const variant = this.#variant(button.dataset.guideValue);
        const state = variant?.available ? 'available' : variant ? 'sold_out' : 'unavailable';
        button.dataset.stock = state;
        button.querySelector('[data-guide-choice-status]').textContent = state === 'available' ? '' : this.#text(state);
        // Sold-out options stay inspectable, but can never be applied.
      }
    }

    #compute() {
      const raw = this.#input?.value || '';
      this.#source = 'measurement';
      this.#choice = null;
      if (!raw.trim()) {
        this.#input.removeAttribute('aria-invalid');
        this.querySelector('[data-guide-result]').hidden = true;
        this.#applyButton.disabled = true;
        this.#clearChoice();
        return;
      }
      this.#calculation = calculate({ values: this.#data.values, raw, unit: this.#unit, ease: this.#ease });
      this.#input.setAttribute('aria-invalid', String(this.#calculation.state === 'invalid'));
      if (this.#calculation.state === 'size') this.#choice = this.#calculation.value;
      this.#renderResult();
    }

    #clearChoice() {
      const buttons = Array.from(this.querySelectorAll('[data-guide-value]'));
      for (const [index, button] of buttons.entries()) {
        const selected = button.dataset.guideValue === this.#choice;
        button.setAttribute('aria-checked', String(selected));
        button.tabIndex = selected || (!this.#choice && index === 0) ? 0 : -1;
      }
    }

    #renderResult() {
      const result = this.querySelector('[data-guide-result]');
      const label = this.querySelector('[data-guide-result-label]');
      const answer = this.querySelector('[data-guide-answer]');
      const detail = this.querySelector('[data-guide-detail]');
      const stock = this.querySelector('[data-guide-stock]');
      result.hidden = false;
      stock.textContent = '';
      detail.textContent = '';
      this.#applyButton.disabled = true;
      this.#clearChoice();
      this.#refreshStock();
      if (!this.#choice) {
        label.textContent = '';
        answer.textContent = this.#text(this.#calculation?.state === 'invalid' ? 'invalid' : this.#calculation?.state === 'no_numeric' ? 'no_numeric' : 'no_size');
        if (this.#calculation?.state === 'out_of_range') detail.textContent = `${this.#text('target')} ${this.#bothUnits(this.#calculation.target)}. ${this.#text('range')} ${this.#bothUnits(this.#calculation.minimum)} – ${this.#bothUnits(this.#calculation.maximum)}.`;
        return;
      }
      const variant = this.#variant(this.#choice);
      const numeric = parseLength(this.#choice);
      label.textContent = this.#text(this.#source === 'measurement' ? 'suggested' : 'selected');
      answer.textContent = this.#choice;
      detail.textContent = numeric !== null ? this.#bothUnits(numeric) : '';
      if (this.#source === 'measurement') detail.textContent += `. ${this.#text('target')} ${this.#bothUnits(this.#calculation.target)}. ${this.#text('next_offered')}`;
      const state = variant?.available ? 'available' : variant ? 'sold_out' : 'unavailable';
      stock.textContent = this.#text(state);
      result.dataset.stock = state;
      this.#applyButton.disabled = !variant?.available;
    }

    async #apply() {
      if (!this.#choice || !this.#variant(this.#choice)?.available) {
        this.#renderResult();
        return;
      }
      this.#applyButton.disabled = true;
      const modal = this.#modal;
      // Close before dispatch: the theme may replace the entire picker after its change event.
      if (modal?.hide) await modal.hide();
      await new Promise((resolve) => setTimeout(resolve, 0));
      // Resolve again after the modal returns to its original parent, preserving fresh options.
      const picker = this.#picker;
      if (!picker || !this.#variant(this.#choice)?.available) return this.#showApplyError();
      const inputs = Array.from(picker.querySelectorAll('input[data-option-index]'));
      let control = inputs.find((input) => Number(input.dataset.optionIndex) === this.#position && input.value === this.#choice && !input.closest('skrt-product-size-guide'));
      let isSelect = false;
      if (!control) {
        for (const select of picker.querySelectorAll('select')) {
          const option = Array.from(select.options).find((item) => Number(item.dataset.optionIndex || select.dataset.optionIndex) === this.#position && item.value === this.#choice);
          if (!option) continue;
          control = select;
          isSelect = true;
          break;
        }
      }
      if (!control || control.disabled) return this.#showApplyError();
      if (!control.isConnected) return;
      if (isSelect) {
        control.value = this.#choice;
        control.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        control.checked = true;
        control.dispatchEvent(new Event('change', { bubbles: true }));
      }
      // The native picker owns price/media/URL updates. Never add the item to the cart here.
      // ProductInfo.renderProductInfo resolves this control's ID after its AJAX swap and
      // restores focus to the replacement control. Keep the native event target intact.
      control.focus({ preventScroll: true });
    }

    #showApplyError() {
      this.querySelector('[data-guide-stock]').textContent = this.#text('manual');
      this.#applyButton.disabled = true;
    }
  }

  window.customElements.define('skrt-product-size-guide', SkrtProductSizeGuide);
})();
